<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Quote Entries</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage and track all quote submissions</p>
      </div>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-download"
        :loading="exporting"
        :disabled="exporting || loading"
        @click="exportCsv"
      >
        Export CSV
      </v-btn>
    </div>

    <!-- Date Range Filter -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-icon color="primary" class="mr-2">mdi-calendar-range</v-icon>
        <span class="text-body-2 font-weight-medium text-medium-emphasis mr-4">Date Range:</span>

        <v-menu v-model="menuStart" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(startDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="startDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <span class="text-body-2 text-medium-emphasis">to</span>

        <v-menu v-model="menuEnd" :close-on-content-click="false" max-width="290px">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              size="small"
              prepend-icon="mdi-calendar"
              class="text-none"
            >
              {{ formatDisplay(endDate) }}
            </v-btn>
          </template>
          <v-date-picker v-model="endDate" @update:modelValue="onDateChange" hide-actions></v-date-picker>
        </v-menu>

        <v-divider vertical class="mx-2 d-none d-sm-block" style="height: 24px;"></v-divider>

        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          density="compact"
          variant="outlined"
          divided
          rounded="lg"
        >
          <v-btn value="7" size="small" @click="setLastDays(7)">7 Days</v-btn>
          <v-btn value="30" size="small" @click="setLastDays(30)">30 Days</v-btn>
          <v-btn value="90" size="small" @click="setLastDays(90)">90 Days</v-btn>
          <v-btn value="all" size="small" @click="setFromBeginning">From the beginning</v-btn>
        </v-btn-toggle>
      </div>
    </v-card>

    <!-- Quotes Timeline -->
    <div class="mb-6">
      <QuotesTimelineApex :start-date="startDate" :end-date="endDate" />
    </div>

    <!-- Filters Card -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 16px;">
        <v-text-field
          v-model="search"
          label="Search by name, email, or phone"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 400px; min-width: 250px;"
          clearable
        ></v-text-field>

        <v-select
          v-model="selectedChannelId"
          :items="channelOptions"
          label="Channel"
          item-title="name"
          item-value="id"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 200px; min-width: 150px;"
          @update:modelValue="onChannelChange"
        ></v-select>

        <v-select
          v-model="selectedMenu"
          :items="menuOptions"
          label="Menu"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
          @update:modelValue="onFilterChange"
        ></v-select>

        <v-select
          v-model="selectedDelivery"
          :items="deliveryOptions"
          label="Delivery Type"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
          @update:modelValue="onFilterChange"
        ></v-select>

        <v-select
          v-model="selectedOccasion"
          :items="occasionOptions"
          label="Occasion"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
          @update:modelValue="onFilterChange"
        ></v-select>

        <v-select
          v-model="selectedPaymentStatus"
          :items="paymentStatusOptions"
          label="Payment"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
          @update:modelValue="onFilterChange"
        ></v-select>

        <v-select
          v-model="selectedCheckoutMatch"
          :items="checkoutMatchOptions"
          label="Checkout"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
          @update:modelValue="onFilterChange"
        ></v-select>

        <v-switch
          v-model="onlyConverted"
          label="Comeback Only"
          inset
          color="warning"
          hide-details
          density="compact"
          @update:modelValue="onConvertedToggle"
        ></v-switch>

        <v-btn
          variant="tonal"
          density="compact"
          prepend-icon="mdi-refresh"
          @click="resetFilters"
        >
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card>
      <v-data-table-server
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="entries"
        :items-length="total"
        :items-per-page-options="[20, 50, 100]"
        :loading="loading"
        :search="search"
        @update:options="loadItems"
      >
        <template #item.id="{ item }">
          <div class="id-col">
            {{ item.id }}
            <v-chip v-if="item.convertedFromCb" size="small" color="warning">
              CB
            </v-chip>
          </div>
        </template>
        <template #item.customer="{ item }">
          {{ item.customer?.name }}
          {{ item.customer?.lastName }}
        </template>
        <template #item.emailAndPhone="{ item }">
          {{ item.customer?.email }} <br />
          {{ item.customer?.phone }}
        </template>
        <template #item.eventDate="{ item }">
          {{ item.date }}
        </template>
        <template #item.createdDate="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #item.comebackOfferId="{ item }">
          <v-chip v-if="item.convertedFromCb" :color="item.convertedFromCb ? 'warning' : ''">
            {{ item.comebackOfferId }}
          </v-chip>
        </template>
        <template #item.addonPackage="{ item }">
          <v-chip v-if="item.addonPackage" size="small" color="teal">
            {{ item.addonPackage.name }}
          </v-chip>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
        <template #item.paymentStatus="{ item }">
          <PaymentStatusChip v-if="item.paymentStatus" :quote="item" />
          <span v-else class="text-medium-emphasis">—</span>
        </template>
        <template #item.channel="{ item }">
          <v-chip
            :color="
              item.channel === 0
                ? 'primary'
                : item.channel === 1
                ? 'green'
                : 'purple'
            "
          >
            {{ item.channelName }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <div class="text-center">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  density="compact"
                  icon="mdi-dots-horizontal"
                ></v-btn>
              </template>
              <v-list>
                <v-list-item @click="previewQuote(item)">
                  <v-list-item-title>Preview</v-list-item-title>
                </v-list-item>
                <v-list-item @click="confirmDelete(item)">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table-server>
      <div v-if="!entries.length && !loading" class="text-center py-8 text-medium-emphasis">
        No quote entries found.
      </div>
    </v-card>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="text-body-1">
          Are you sure you want to delete this quote entry? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteEntry">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { quoteService, channelService, statisticsService } from "../services";
import { useNotificationStore } from "../stores";
import { useDebounceSearch } from "../composables/useDebounceSearch";
import { getOccasionOptions } from "../constants/occasions";
import QuotesTimelineApex from "../components/QuotesTimelineApex.vue";
import PaymentStatusChip from "../components/PaymentStatusChip.vue";

defineOptions({ name: "QuoteEntriesView" });

const notificationStore = useNotificationStore();

const router = useRouter();

const entries = ref([]);
const page = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);
const totalPages = ref(0);
const loading = ref(false);
const exporting = ref(false);
const showDeleteConfirm = ref(false);
const entryToDelete = ref(null);
const search = ref("");
const selectedChannelId = ref(null);
const onlyConverted = ref(false);
const channels = ref([]);
const selectedMenu = ref("");
const selectedDelivery = ref("");
const selectedOccasion = ref("");
const selectedPaymentStatus = ref("");
const selectedCheckoutMatch = ref("");
const menuOptions = ref([{ title: "All Menus", value: "" }]);

const deliveryOptions = [
  { title: "All Delivery Types", value: "" },
  { title: "Drop off per person", value: "Drop off per person" },
  { title: "Pickup per person", value: "Pickup per person" },
  { title: "On-site cooking", value: "On-site cooking" },
];

const paymentStatusOptions = [
  { title: "All Payments", value: "" },
  { title: "Checkout completed", value: "paid" },
  { title: "Checkout started", value: "pending" },
  { title: "Refunded", value: "refunded" },
  { title: "Voided", value: "voided" },
  { title: "No online payment", value: "none" },
];

// Was the paid Foxy cart the one that was signed?
const checkoutMatchOptions = [
  { title: "Any checkout", value: "" },
  { title: "Changed at checkout", value: "changed" },
  { title: "Paid as quoted", value: "unchanged" },
  { title: "Not compared", value: "unknown" },
];

const occasionOptions = computed(() => [
  { title: "All Occasions", value: "" },
  ...getOccasionOptions().map((o) => ({ title: o.title, value: o.title })),
]);

const startDate = ref(new Date(new Date().getTime() - 29 * 24 * 60 * 60 * 1000));
const endDate = ref(new Date());
const selectedPeriod = ref("30");
const menuStart = ref(false);
const menuEnd = ref(false);

function formatDisplay(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function toIsoDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function onDateChange() {
  if (startDate.value && endDate.value && startDate.value > endDate.value) {
    endDate.value = startDate.value;
  }
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

function setLastDays(days) {
  const end = new Date();
  const start = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  startDate.value = start;
  endDate.value = end;
  selectedPeriod.value = String(days);
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

function setFromBeginning() {
  startDate.value = new Date(2025, 7, 18); // August 18th 2025
  endDate.value = new Date();
  selectedPeriod.value = "all";
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

const headers = [
  { title: "ID", key: "id" },
  { title: "Created Date", key: "createdDate" },
  { title: "Customer", key: "customer" },
  { title: "Email & Phone", key: "emailAndPhone" },
  { title: "Event Date", key: "eventDate" },
  { title: "Delivery", key: "delivery" },
  { title: "Menu Name", key: "menuName" },
  { title: "Guests", key: "numberOfGuests" },
  { title: "Total Cost", key: "totalCost" },
  { title: "Payment", key: "paymentStatus", sortable: false },
  { title: "Addon Package", key: "addonPackage" },
  { title: "Channel", key: "channel" },
  { title: "Comeback Id", key: "comebackOfferId" },
  { title: "Actions", key: "actions", sortable: false },
];

const channelOptions = computed(() => {
  const base = [{ id: null, name: "All Channels" }];
  return [...base, ...channels.value];
});

function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  return (
    d.toLocaleDateString() +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

async function loadEntries(targetPage = 1, pageSize = 20) {
  loading.value = true;
  try {
    const response = await quoteService.getQuoteEntries({
      page: targetPage,
      limit: pageSize,
      search: search.value,
      channelId: selectedChannelId.value,
      convertedOnly: onlyConverted.value,
      menuName: selectedMenu.value,
      delivery: selectedDelivery.value,
      occasion: selectedOccasion.value,
      paymentStatus: selectedPaymentStatus.value,
      checkoutMatch: selectedCheckoutMatch.value,
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });
    entries.value = response.data || response;
    total.value = response.total || 0;
    page.value = response.page || targetPage;
    itemsPerPage.value = response.pageSize || pageSize;
    totalPages.value =
      response.totalPages ||
      (itemsPerPage.value ? Math.ceil(total.value / itemsPerPage.value) : 0);
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to load quote entries");
    entries.value = [];
    total.value = 0;
    totalPages.value = 0;
  } finally {
    loading.value = false;
  }
}

useDebounceSearch(search, () => {
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
});

function onChannelChange() {
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

function onConvertedToggle() {
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

function onFilterChange() {
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

function resetFilters() {
  search.value = "";
  selectedChannelId.value = null;
  selectedMenu.value = "";
  selectedDelivery.value = "";
  selectedOccasion.value = "";
  selectedPaymentStatus.value = "";
  selectedCheckoutMatch.value = "";
  onlyConverted.value = false;
  page.value = 1;
  loadEntries(1, itemsPerPage.value);
}

async function loadMenuOptions() {
  try {
    const response = await statisticsService.getQuotesByMenu({});
    const names = (response?.data?.menus || []).map((m) => m.menuName).filter(Boolean);
    menuOptions.value = [
      { title: "All Menus", value: "" },
      ...names.map((n) => ({ title: n, value: n })),
    ];
  } catch {
    menuOptions.value = [{ title: "All Menus", value: "" }];
  }
}

function confirmDelete(entry) {
  entryToDelete.value = entry;
  showDeleteConfirm.value = true;
}

function previewQuote(entry) {
  router.push({ name: "QuoteEntryPreview", params: { id: entry.id } });
}

async function deleteEntry() {
  try {
    await quoteService.deleteQuoteEntry(entryToDelete.value.id);
    showDeleteConfirm.value = false;
    entryToDelete.value = null;
    notificationStore.showSuccess("Quote entry deleted successfully");
    loadEntries(page.value, itemsPerPage.value);
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to delete quote entry");
  }
}

async function loadChannels() {
  try {
    const response = await channelService.getChannels();
    channels.value = response.data || response || [];
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to load channels");
    channels.value = [];
  }
}

function loadItems(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  loadEntries(page.value, itemsPerPage.value);
}

async function exportCsv() {
  exporting.value = true;
  try {
    const { blob, filename } = await quoteService.exportQuoteEntriesCsv({
      search: search.value,
      channelId: selectedChannelId.value,
      convertedOnly: onlyConverted.value,
      menuName: selectedMenu.value,
      delivery: selectedDelivery.value,
      occasion: selectedOccasion.value,
      paymentStatus: selectedPaymentStatus.value,
      checkoutMatch: selectedCheckoutMatch.value,
      startDate: toIsoDate(startDate.value),
      endDate: toIsoDate(endDate.value),
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notificationStore.showSuccess("Quote entries exported successfully");
  } catch (e) {
    notificationStore.handleApiError(e, "Failed to export quote entries");
  } finally {
    exporting.value = false;
  }
}

onMounted(() => {
  loadChannels();
  loadMenuOptions();
  loadEntries(page.value, itemsPerPage.value);
});
</script>

<style scoped>
.id-col {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
}

:deep(.v-data-table) {
  border-radius: 0 0 12px 12px;
}

:deep(.v-data-table-header) {
  background-color: #f8fafc;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #64748b !important;
}
</style>
